import React, { useId, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import PropTypes from 'prop-types';

import { useShipperOptions } from 'components/procurement/components/purchaseOrdersV2/queries/PurchaseOrderQueries';
import { Combobox } from 'shadcn/components/ui/Combobox';

/**
 * Renders a shipper picker select component.
 *
 * @param {Object} props Component props.
 * @param {string|number|null} [props.value] Selected shipper ID.
 * @param {Function} [props.onValueChange] Change handler invoked with the selected shipper ID.
 * @param {string} [props.label='Shipper'] Visible label rendered above the picker.
 * @param {boolean} [props.showLabel=true] Shows the label when true, hides it when false.
 * @returns {JSX.Element} Shipper picker component.
 */
export function ShippersPicker({ label = 'Shipper', onValueChange, showLabel = true, value }) {
    const labelId = useId();
    const { data: shipperOptions = [], isFetching, isLoading } = useShipperOptions();

    // Options are already formatted as {value, label} from useShipperOptions
    // Add "None" option at the beginning to allow clearing the selection
    const options = useMemo(() => {
        const shipperOptionsList = shipperOptions.map((option) => ({
            value: String(option.value),
            label: option.label,
        }));
        return [{ value: '', label: 'None' }, ...shipperOptionsList];
    }, [shipperOptions]);

    /**
     * Extracts the ID from a value.
     * @param {string|number|null} val - The value to extract the ID from.
     * @returns {string|null} The ID as a string, or null if the value is null.
     */
    const extractId = (val) => {
        if (val == null) return null;
        return String(val);
    };

    // Derive selected value as ID string (for Combobox)
    const selectedValue = useMemo(() => {
        if (!value) return '';
        return extractId(value) ?? '';
    }, [value]);

    // Handle select change - return shipper ID or empty string for "None"
    const handleValueChange = (newValue) => {
        if (onValueChange) {
            if (newValue === '' || !newValue) {
                // "None" selected - send empty string
                onValueChange('');
            } else {
                const shipperId = Number(newValue);
                onValueChange(shipperId);
            }
        }
    };

    // Show loading spinner when fetching and no data (or data is empty placeholder)
    const showLoading = isLoading || (isFetching && shipperOptions.length === 0);

    if (showLoading) {
        return (
            <div className="flex flex-col gap-2">
                {showLabel && label && (
                    <span
                        id={labelId}
                        className="text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                    >
                        {label}
                    </span>
                )}
                <div className="flex h-10 w-full items-center justify-center gap-2 rounded-md border border-input bg-card px-3 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    <span className="text-sm">Loading shippers…</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            {showLabel && label && (
                <span
                    id={labelId}
                    className="text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                    {label}
                </span>
            )}
            <Combobox
                id={labelId}
                options={options}
                value={selectedValue}
                handleSelect={handleValueChange}
                placeholder="Select shipper"
                disabled={!options.length || showLoading}
                popoverWidth="trigger"
                className="w-full"
                commandProps={{
                    filter: (optionValue, search) => {
                        // Filter by label (shipper name)
                        const option = options.find((opt) => String(opt.value) === String(optionValue));
                        if (!option) return false;
                        // Allow "None" to be searchable
                        if (option.value === '') {
                            return 'none'.includes(search.toLowerCase());
                        }
                        return option.label.toLowerCase().includes(search.toLowerCase());
                    },
                }}
            />
        </div>
    );
}

ShippersPicker.propTypes = {
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    onValueChange: PropTypes.func,
    showLabel: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ShippersPicker.defaultProps = {
    label: 'Shipper',
    onValueChange: undefined,
    showLabel: true,
    value: undefined,
};

ShippersPicker.displayName = 'ShippersPicker';
